import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { fromEvent } from 'rxjs';
import { throttleTime, share } from 'rxjs/operators';

import AlertStructuresInDescription from './AlertStructuresInDescription';
import AlertToggleButton from './AlertToggleButton';

import './AlertDescription.scss';

const resize$ = fromEvent(window, 'resize').pipe(throttleTime(300), share());

function AlertDescription(props) {
  const { description, showLabel, hideLabel } = props;
  const [open, setOpen] = useState(false);
  const [showToggleButton, setShowToggleButton] = useState(false);
  const contentWrapperRef = useRef(null);
  const descriptionRef = useRef(null);
  const detailsId = useId();

  const toggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const calcToggleButton = useCallback(() => {
    if (open === false) {
      const contentWrapperWidth = contentWrapperRef.current?.clientWidth || contentWrapperRef.current?.offsetWidth || 0;
      const descriptionWidth = descriptionRef.current?.clientWidth || descriptionRef.current?.offsetWidth || 0;
      const contentWrapperOverflows = contentWrapperRef.current && contentWrapperRef.current.scrollWidth > contentWrapperWidth;
      const descriptionOverflows = descriptionRef.current && descriptionRef.current.scrollWidth > descriptionWidth;
      const shouldShowToggleButton = Boolean(contentWrapperOverflows || descriptionOverflows);

      setShowToggleButton(shouldShowToggleButton);
    }
  }, [open]);

  useEffect(() => {
    calcToggleButton();
    const resizeStream$ = resize$.subscribe(calcToggleButton);
    return () => {
      resizeStream$.unsubscribe();
    };
  }, [calcToggleButton]);

  const isCollapsedForAllUsers = showToggleButton && !open;

  return (
    <section
      className={classnames('alert-description alerts--second-line clearfix', {
        'alert-description--collapsed': !open,
        'alert-description--padding-for-toggle': !open && showToggleButton,
      })}>
      <div ref={contentWrapperRef} id={detailsId} className="alert-description--content-wrapper" aria-hidden={isCollapsedForAllUsers}>
        <p ref={descriptionRef} className="alert-description--text">
          {description}
        </p>
        <AlertStructuresInDescription {...props} />
      </div>
      {showToggleButton && (
        <AlertToggleButton open={open} toggle={toggle} showLabel={showLabel} hideLabel={hideLabel} controlsId={detailsId} />
      )}
    </section>
  );
}

AlertDescription.propTypes = {
  description: PropTypes.string,
  showLabel: PropTypes.string.isRequired,
  hideLabel: PropTypes.string.isRequired,
};

export default AlertDescription;
